import { FamilyApplicationDetails } from '@/queries/family_applications/requests'
import Image from '@/common/components/Image'
import { format, parseISO } from 'date-fns'
import { Input } from '@/app/common/components/input/Input'
import { useState } from 'react'

type IDocument = {
  id: number
  image_name_front: string
  variation: string
  created_datetime: string
  first_name?: string
  last_name?: string
  document_owner?: string
}

export default function ApplicantDocumentsView({
  ApplicantData,
}: {
  ApplicantData: FamilyApplicationDetails
}) {
  const [query, setQuery] = useState('')
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const getFullName = (
    firstName?: string | null,
    lastName?: string | null
  ): string => {
    return `${firstName ?? ''} ${lastName ?? ''}`
  }

  const getVariation = (variation: string) => {
    return variation.replace(/TAX_INCOME.*/, 'TAX INCOME').replace(/_/g, ' ')
  }

  const searchQuery = (doc: IDocument) => {
    return (
      doc.variation.toLowerCase().includes(query.toLocaleLowerCase()) ||
      doc.first_name?.toLowerCase().includes(query.toLocaleLowerCase()) ||
      doc.last_name?.toLowerCase().includes(query.toLocaleLowerCase())
    )
  }

  const ensureTimestampFormat = (timestamp: string): string => {
    if (!timestamp.endsWith('.000000Z')) {
      return timestamp.replace(' ', 'T') + '.000000Z'
    }
    return timestamp
  }
  const family_documents = ApplicantData.family_documents?.filter((doc) =>
    searchQuery(doc as IDocument)
  ) as IDocument[]
  const students_documents = ApplicantData.students_documents?.filter((doc) =>
    searchQuery(doc as IDocument)
  ) as IDocument[]
  const allDocuments = family_documents
    .concat(students_documents)
    .sort(
      (a, b) =>
        new Date(ensureTimestampFormat(b.created_datetime)).getTime() -
        new Date(ensureTimestampFormat(a.created_datetime)).getTime()
    )
  return (
    <>
      <Input
        placeholder="Search by document type or student name..."
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
      />

      <ul
        role="list"
        className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 mt-5"
      >
        {allDocuments?.map((doc) => (
          <li key={doc.id} className="relative">
            <div
              className="group overflow-hidden rounded-lg cursor-pointer border border-gray-300 p-2 flex flex-col items-center"
              onClick={() => setSelectedImage(doc.image_name_front)}
            >
              {!selectedImage && (
                <>
                  <div className="w-32 h-32 bg-gray-200 flex items-center justify-center rounded-md">
                    <Image
                      alt="Document Preview"
                      className="w-16 h-16 opacity-70"
                    />
                  </div>
                  <span className="absolute top-2 right-2 bg-white text-xs font-semibold px-2 py-1 shadow rounded">
                    View
                  </span>
                </>
              )}
            </div>

            <p className="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900">
              {getVariation(doc.variation)}
            </p>

            <p className="pointer-events-none block text-sm font-medium text-gray-500">
              {format(
                parseISO(ensureTimestampFormat(doc.created_datetime)),
                'MM/dd/yyyy, h:mm aa'
              )}
            </p>
            {(doc.first_name || doc.last_name) && (
              <p className="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900">
                {getFullName(doc.first_name, doc.last_name)}
              </p>
            )}
            {doc.document_owner == 'spouse' &&
              (ApplicantData.spouse_name || ApplicantData.spouse_last_name) && (
                <p className="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900">
                  {getFullName(
                    ApplicantData.spouse_name,
                    ApplicantData.spouse_last_name
                  )}
                </p>
              )}
          </li>
        ))}
      </ul>
      {selectedImage && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative">
            <button
              className="absolute top-2 right-2 bg-white p-1 px-2 shadow rounded text-black shadow-lg z-50"
              onClick={(e) => {
                e.stopPropagation()
                setSelectedImage(null)
              }}
            >
              ✕
            </button>
            <Image
              path={selectedImage}
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  )
}
